import { useEffect, useMemo, useRef } from 'react';
import type { CreatorStreamNotification } from '@rbx/clients/creatorNotificationStreamsApi';
import type { TSendEvent } from '../../providers/EventProvider';
import { notificationItemImpressionEventModel } from '../../event/eventConstants';

const OUT_OF_VIEW_THRESHOLD_MS = 60000;

// NOTE (neoxu, 10/30/2025): This hook is similiar to knowledge-feed/src/hooks/useCardImpressionTracker.tsx
function useNotificationImpressionTracker(
  ref: React.RefObject<HTMLElement>,
  notification: CreatorStreamNotification | null | undefined,
  sendEvent: TSendEvent,
  notificationGroupIndex?: number,
): void {
  const lastViewByIdRef = useRef<Record<string, number>>({});
  const timeoutRef = useRef<number | null>(null);

  const debouncedLogImpression = useMemo(() => {
    const log = () => {
      if (notification) {
        sendEvent(notificationItemImpressionEventModel(notification, notificationGroupIndex));
      }
    };

    const debounced = () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(log, 100);
    };

    const cancel = () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    return { debounced, cancel };
  }, [notification, sendEvent, notificationGroupIndex]);

  useEffect(() => {
    const node = ref.current;
    if (!node || !notification) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const key = notification.notificationId ?? '';
            const lastView = lastViewByIdRef.current[key];
            if (!lastView || Date.now() - lastView >= OUT_OF_VIEW_THRESHOLD_MS) {
              debouncedLogImpression.debounced();
            }
            lastViewByIdRef.current[key] = Date.now();
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5, // fire when 50% visible
      },
    );

    observer.observe(node);
    return () => {
      observer.unobserve(node);
      observer.disconnect();
      debouncedLogImpression.cancel();
    };
  }, [ref, notification, debouncedLogImpression, notificationGroupIndex]);
}

export default useNotificationImpressionTracker;


